import { ChangeEvent, FormEvent, useEffect, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { AdminPageHeader } from '../../components/admin/AdminPageHeader';
import { DateRangeFilter } from '../../components/admin/DateRangeFilter';
import { brandChipSx } from '../../components/admin/brandChip';
import { DateRangeValue, EMPTY_DATE_RANGE, dateRangeToIso } from '../../components/admin/dateRange';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { auditLogReducer } from '../../store/reducers/auditLog.reducer';
import { userReducer } from '../../store/reducers/user.reducer';
import {
  getAuditLogs,
  getAuditLogsError,
  getAuditLogsPage,
  getAuditLogsPageSize,
  getAuditLogsTotalElements,
  getIsLoadingAuditLogs,
} from '../../store/selectors/auditLog.selector';
import { getAllUsers, getUser } from '../../store/selectors/user.selector';
import { history } from '../../store/store';
import { AUDIT_ACTIONS, AUDIT_ENTITY_TYPES, AuditLogFilters, AuditStatus } from '../../types/auditLog';
import { User } from '../../types/user';

const { loadAuditLogs } = auditLogReducer.actions;
const { loadUsers } = userReducer.actions;

const DEFAULT_PAGE_SIZE = 20;
const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

const emptyFilters: AuditLogFilters = {
  userId: undefined,
  action: '',
  entityType: '',
  entityId: '',
  status: undefined,
  from: '',
  to: '',
};

export const AuditLogsPage = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const auditLogs = useAppSelector(getAuditLogs);
  const isLoading = useAppSelector(getIsLoadingAuditLogs);
  const error = useAppSelector(getAuditLogsError);
  const page = useAppSelector(getAuditLogsPage);
  const pageSize = useAppSelector(getAuditLogsPageSize);
  const totalElements = useAppSelector(getAuditLogsTotalElements);

  const allUsers = useAppSelector(getAllUsers);

  const [filters, setFilters] = useState<AuditLogFilters>(emptyFilters);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [timeRange, setTimeRange] = useState<DateRangeValue>(EMPTY_DATE_RANGE);
  const [appliedFilters, setAppliedFilters] = useState<AuditLogFilters>(emptyFilters);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Admin guard
  useEffect(() => {
    if (user && !user.roles.includes('ADMIN')) {
      history.push('/forbidden');
    }
  }, [user]);

  // Load users for autocomplete
  useEffect(() => {
    if (user && user.roles.includes('ADMIN')) {
      dispatch(loadUsers());
    }
  }, [dispatch, user]);

  // Fetch when applied filters / page / size change
  useEffect(() => {
    if (user && user.roles.includes('ADMIN')) {
      dispatch(
        loadAuditLogs({
          ...appliedFilters,
          page: currentPage,
          pageSize: currentPageSize,
        }),
      );
    }
  }, [dispatch, user, appliedFilters, currentPage, currentPageSize]);

  if (!user || !user.roles.includes('ADMIN')) {
    return null;
  }

  const handleFilterChange =
    (key: keyof AuditLogFilters) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFilters((prev) => ({
        ...prev,
        [key]: key === 'userId' ? (value === '' ? undefined : Number(value)) : value,
      }));
    };

  const handleStatusChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value as AuditStatus | '';
    setFilters((prev) => ({ ...prev, status: value === '' ? undefined : (value as AuditStatus) }));
  };

  const handleUserChange = (_event: unknown, value: User | null) => {
    setSelectedUser(value);
    setFilters((prev) => ({ ...prev, userId: value?.id }));
  };

  const handleTimeRangeChange = (value: DateRangeValue) => {
    setTimeRange(value);
    setFilters((prev) => ({ ...prev, from: value.from, to: value.to }));
  };

  const handleApply = () => {
    setCurrentPage(0);
    const { from, to } = dateRangeToIso(timeRange);
    setAppliedFilters({ ...filters, from, to });
  };

  const handleReset = () => {
    setFilters(emptyFilters);
    setSelectedUser(null);
    setTimeRange(EMPTY_DATE_RANGE);
    setAppliedFilters(emptyFilters);
    setCurrentPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setCurrentPageSize(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const formatDateTime = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString();
  };

  return (
    <Box>
      <AdminPageHeader title="Audit Logs" subtitle="Inspect system audit logs (admin only)." />

      {/* A form, so Enter in any filter applies them like the Apply button. */}
      <Paper
        component="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          handleApply();
        }}
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid rgba(255, 255, 255, 0.08)',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
        }}
      >
        <Autocomplete
          size="small"
          options={allUsers}
          value={selectedUser}
          onChange={handleUserChange}
          getOptionLabel={(option) => option.email}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2">{option.email}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  {option.fullName} · #{option.id}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => <TextField {...params} label="User (email)" />}
        />
        <TextField
          label="Action"
          select
          size="small"
          value={filters.action ?? ''}
          onChange={handleFilterChange('action')}
        >
          <MenuItem value="">All</MenuItem>
          {AUDIT_ACTIONS.map((a) => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Entity Type"
          select
          size="small"
          value={filters.entityType ?? ''}
          onChange={handleFilterChange('entityType')}
        >
          <MenuItem value="">All</MenuItem>
          {AUDIT_ENTITY_TYPES.map((e) => (
            <MenuItem key={e} value={e}>
              {e}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Entity ID"
          size="small"
          value={filters.entityId ?? ''}
          onChange={handleFilterChange('entityId')}
        />
        <TextField label="Status" select size="small" value={filters.status ?? ''} onChange={handleStatusChange}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="SUCCESS">SUCCESS</MenuItem>
          <MenuItem value="FAILURE">FAILURE</MenuItem>
        </TextField>
        <DateRangeFilter label="Time" mode="datetime" value={timeRange} onChange={handleTimeRangeChange} fullWidth />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            sx={{
              bgcolor: '#B3EC49',
              color: 'black',
              '&:hover': { bgcolor: 'rgba(179, 236, 73, 0.8)' },
              borderRadius: '24px',
              px: 3,
            }}
            type="submit"
          >
            Apply
          </Button>
          <Button type="button" variant="outlined" onClick={handleReset} sx={{ borderRadius: '24px', px: 3 }}>
            Reset
          </Button>
        </Box>
      </Paper>

      {isLoading && auditLogs.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!isLoading && !error && auditLogs.length === 0 && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No audit logs found
          </Typography>
        </Box>
      )}

      {!error && auditLogs.length > 0 && (
        <Paper elevation={0} sx={{ border: '1px solid rgba(255, 255, 255, 0.08)' }}>
          {/* Keep the rows while the next page or filter loads, instead of swapping them for a spinner. */}
          <Box sx={{ height: 2 }}>{isLoading && <LinearProgress sx={{ height: 2 }} />}</Box>
          <TableContainer sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 150ms' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Occurred At</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>HTTP</TableCell>
                  <TableCell>IP</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(log.occurredAt)}</TableCell>
                    <TableCell>
                      {log.userEmail ?? '-'}
                      {log.userId !== null && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.5)' }}>
                          #{log.userId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{log.action}</TableCell>
                    <TableCell>
                      {log.entityType ?? '-'}
                      {log.entityId && (
                        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.5)' }}>
                          {log.entityId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {log.httpMethod ?? ''} {log.httpPath ?? ''}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{log.ipAddress ?? '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={log.status}
                        sx={brandChipSx(log.status === 'SUCCESS' ? 'primary' : 'outlined')}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      {log.details ? (
                        <Tooltip title={log.details} placement="top">
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {log.details}
                          </Typography>
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={pageSize}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </Paper>
      )}
    </Box>
  );
};
